/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import _ from 'lodash';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { EnvService } from 'src/app/env.service';
import { AuthService, LoginGrantType, LoginRequest, SSORequestApplicationEnum, User, UserRole } from 'src/openapi';
import { Global, MenuMap, SectionMap } from '../global';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private currentTokenSubject: BehaviorSubject<string>;
  public currentUserSubject: BehaviorSubject<User>;

  public get currentToken(): string {
    return this.currentTokenSubject.value;
  }

  public get currentUser(): User {
    return this.currentUserSubject.value;
  }

  constructor(
    private env: EnvService,
    private router: Router,
    /*private loginGQL: LoginGQL,
    private ssoLoginGQL: SsoLoginGQL,
    private currentUserGQL: CurrentUserGQL,
    private impersonateGQL: ImpersonateGQL,
    private deimpersonateGQL: DeimpersonateGQL,*/
    private authService: AuthService
  ) {
    try {
      const currentToken =
        // sessionStorage.getItem('currentToken') ||
        localStorage.getItem('currentToken');
      this.currentTokenSubject = new BehaviorSubject<string>(currentToken);
      this.currentUserSubject = new BehaviorSubject<User>(null);

      if (currentToken !== null && this.currentUser === null) {
        this.fetchUserData();
      }
    } catch (error) {
      localStorage.setItem('sessionExpired', 'true');
      localStorage.removeItem('currentToken');
      location.reload();
    }
  }

  login(username: string, password: string, remember: boolean): Observable<string> {
    const request: LoginRequest = {
      grantType: LoginGrantType.Password,
      clientId: this.env.CLIENT_ID,
      clientSecret: this.env.CLIENT_SECRET,
      username,
      password,
      scope: 'co',
    };

    return this.authService.login(request).pipe(
      map(
        (response) => {
          const token = response.accessToken;
          if (token) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            if (remember) {
              localStorage.setItem('currentToken', token);
            } else {
              localStorage.setItem('currentToken', token);
              // sessionStorage.setItem('currentToken', token);
            }
            this.currentTokenSubject.next(token);
            this.fetchUserData();
          }

          return token;
        },
        (error) => {
          console.error(error);

          return error;
        }
      )
    );
  }

  /*loginMiddleware(
    username: string,
    password: string,
    remember: boolean
  ): Observable<string> {
    const request: LoginRequest = {
      grantType: LoginGrantType.Password,
      clientId: this.env.CLIENT_ID,
      clientSecret: this.env.CLIENT_SECRET,
      username,
      password,
      scope: '*',
    };
    //console.log('Logging in...');

    return this.loginGQL.mutate({ loginRequest: request }).pipe(
      map(
        (response) => {
          const token = response.data?.login?.accessToken;
          if (token) {
            Global.isLoggedInFromSSO = false;
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            if (remember) {
              localStorage.setItem('currentToken', token);
            } else {
              sessionStorage.setItem('currentToken', token);
            }
            this.currentTokenSubject.next(token);
            this.fetchUserData();
          }
          return token;
        },
        (error) => {
          console.error(error);
          return error;
        }
      )
    );
  }*/

  ssoLogin(token: string, remember: boolean): Observable<boolean> {
    return this.authService
      .sso({
        token: token,
        application: SSORequestApplicationEnum.Co,
      })
      .pipe(
        switchMap((response) => {
          Global.isLoggedInFromSSO = true;
          return this.forceLogin$(response?.accessToken, remember);
        }),
        catchError((error) => {
          console.error(error);
          return throwError(error);
        })
      );
  }

  impersonate(userId: number): Observable<string> {
    return this.authService.impersonateTakeByUserId(userId, 'co').pipe(
      map((response) => {
        const token = response.data.token;
        if (token) {
          this.forceLogin$(token, false, true).subscribe(
            () => {
              this.router.navigate(['/']);
            },
            (error) => {
              console.error(error);
            }
          );
        }
        return token;
      }),
      // filter(result => !result.loading),
      catchError((e) => {
        console.error(e);
        return throwError(e);
      })
    );
  }

  deimpersonate(): Observable<string> {
    return this.authService.impersonateLeave().pipe(
      map((response) => {
        const token = response.data.token;
        if (token) {
          this.logout();
          this.forceLogin$(token, true, true).subscribe(
            () => {
              this.router.navigate(['/']);
            },
            (error) => {
              console.error(error);
            }
          );
        }
        return token;
      }),
      // filter(result => !result.loading),
      catchError(({ graphQLErrors, networkError, response }) => {
        let error: any;
        if (graphQLErrors) {
          error = graphQLErrors[0].extensions?.response?.body;
        }
        //   if (err.networkError) { ... }
        // default return
        return throwError(error);
      })
    );
  }

  private forceLogin$(token: string, remember: boolean, refetch: boolean = false): Observable<boolean> {
    if (token) {
      // store user details and jwt token in local storage to keep user logged in between page refreshes
      if (remember) {
        localStorage.setItem('currentToken', token);
      } else {
        localStorage.setItem('currentToken', token);
        // sessionStorage.setItem('currentToken', token);
      }
      this.currentTokenSubject.next(token);
      return this.fetchUserData$().pipe(map(() => true));
    }
    return of(true);
  }

  logout(): void {
    //console.log('Logging out...');
    // remove user from local storage to log user out
    localStorage.removeItem('currentToken');
    // sessionStorage.removeItem('currentToken');
    this.currentTokenSubject.next(null);
  }

  fetchUserData(): void {
    this.fetchUserData$().subscribe(
      (user) => {},
      (error) => {}
    );
  }

  fetchUserData$(): Observable<User> {
    return this.authService.getCurrentUser(true).pipe(
      map(
        (response) => {
          this.currentUserSubject.next(response.data);
          return response.data;
        },
        (error) => {
          console.error(error);
          this.router.navigate(['/critical-error']);
          return error;
        }
      ),
      catchError((error) => {
        console.error(error);
        this.router.navigate(['/critical-error']);
        return throwError(error);
      })
    );
  }

  userCan(permission: string): boolean {
    return this.currentUser && this.currentUser.permissions && this.currentUser.permissions.includes(permission);
  }

  userHasRole(role: UserRole): boolean {
    return this.currentUser && this.currentUser.roles && this.currentUser.roles.includes(role);
  }

  getMenuVisibilityForUser(section: string, menu: string[]): boolean {
    const retrievedSection: SectionMap = Global.menuRolesMap.sections.find((f) => f.name == section);

    let allowed: boolean = false;

    if (_.intersection(retrievedSection?.allowedRoles ?? [], this.currentUser.roles).length > 0) {
      allowed = true;
    }

    let currentMenu: MenuMap = undefined;
    menu.every((fe: string) => {
      currentMenu = (currentMenu ?? retrievedSection).menu.find((f) => f.name == fe);
      if (!currentMenu) {
        allowed = false;
        return false;
      }

      if (_.intersection(currentMenu?.allowedRoles ?? [], this.currentUser.roles).length > 0) {
        allowed = true;
      }

      if (_.intersection(currentMenu?.preventRoles ?? [], this.currentUser.roles).length > 0) {
        allowed = false;
      }

      if (!allowed) {
        return false;
      }

      return true;
    });

    return allowed;
  }
}
